import React from 'react';
import FullCalendar from '@fullcalendar/react'; // must go before plugins
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import './calendar.scss';
import CalendarEvent from '../../models/CalendarEvent';
import { CalendarService } from '../../api';
import { useHistory } from 'react-router-dom';
import { DataLoadingContext } from '../../components/DataLoading';
import { AuthContext } from '../Authentication/state';
import { UserRole } from '../../models/User';
import CalendarSelector, {
    CalendarState,
} from '../../components/ViewSelector/CalendarSelector';
import ProjectTypeLegend from './ProjectTypeLegend';
import Button from '../../components/Button';
import { ReactComponent as GoogleIcon } from '../../assets/icons/google.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh.svg';
import Modal from '../../components/Modal';
import Typography from '../../components/Typography';
import { NotificationContext } from '../../components/Notification/state';

const Calendar: React.FC = () => {
    const { toggleLoading } = React.useContext(DataLoadingContext);
    const { user } = React.useContext(AuthContext).state;
    const isAdminManager =
        user &&
        (user.role === UserRole.Admin || user.role === UserRole.Manager);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const calanderRef = React.useRef<any>(null);
    const [events, setEvents] = React.useState<CalendarEvent[]>([]);
    const [active, setActive] = React.useState<CalendarState>(
        isAdminManager ? 'Events Calendar' : 'Your Calendar',
    );
    const [loading, steLoading] = React.useState(-1);
    const [openModal, setOpenModal] = React.useState(false);
    const notification =
        React.useContext(NotificationContext).notificationDispatch;
    const history = useHistory();

    React.useEffect(() => {
        getData();
    }, [active]);

    const getData = () => {
        toggleLoading(true);
        const request = determineRequest();
        request
            .then((response) => {
                setEvents(response);
            })
            .finally(() => toggleLoading(false));
    };

    const determineRequest = () => {
        switch (active) {
            case 'Your Calendar':
                return CalendarService.getMyCalendarEvents();
            case 'Events Calendar':
                return CalendarService.getCalendarEvents();
            default:
                return CalendarService.getCalendarEvents();
        }
    };

    const handleActiveChange = (el: CalendarState) => {
        setActive(el);
        setEvents([]);
        if (el === 'Your Calendar')
            calanderRef.current.getApi().changeView('dayGridMonth');
        if (el === 'Events Calendar')
            calanderRef.current.getApi().changeView('listMonth');
    };

    const handleChange = (value: string) => {
        if (calanderRef.current) {
            if (value === 'today') {
                calanderRef.current.getApi().today();
            } else {
                calanderRef.current.getApi().changeView(value);
            }
        }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClick = (info: any) => {
        history.push(`/campaigns/${info.event.id}/detail`);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventContent = (e: any) => {
        const temp = e.event.extendedProps;
        return `${temp.projectName} - ${e.event.title}`;
    };

    const handleSubscribeToGoogleCalendar = () => {
        steLoading(1);

        CalendarService.subscribeToCalendar()
            .then((calendarUrl) => {
                setOpenModal(false);
                window.location.href = calendarUrl;
            })
            .finally(() => {
                steLoading(-1);
            });
    };

    const syncGoogleCalendar = () => {
        steLoading(2);
        CalendarService.syncGoogleCalendar()
            .then((response) => {
                if (response.totalEvents !== response.syncedEvents) {
                    notification({
                        type: 0,
                        payload: {
                            text: `Synced ${response.syncedEvents} out of ${response.totalEvents} events.`,
                            status: 'warning',
                        },
                    });
                } else {
                    notification({
                        type: 0,
                        payload: {
                            text: `Synced ${response.syncedEvents} events successfully.`,
                            status: 'success',
                        },
                    });
                }
            })
            .finally(() => {
                steLoading(-1);
            });
    };

    return (
        <div className="calendar-container">
            <div className="calendar-header">
                <ProjectTypeLegend />
                {(user?.role === UserRole.Admin ||
                    user?.role === UserRole.Manager) && (
                    <div style={{ display: 'grid', gap: '12px' }}>
                        <Button
                            color={loading === 1 ? 'primary' : 'white'}
                            textColor={loading === 1 ? 'white' : 'primary'}
                            className="export-google"
                            onClick={() => setOpenModal(true)}
                            loading={loading === 1}
                        >
                            <GoogleIcon />
                            Subscribe
                        </Button>
                        {user && user.role === UserRole.Admin && (
                            <Button
                                color={loading === 2 ? 'primary' : 'white'}
                                textColor={loading === 2 ? 'white' : 'primary'}
                                className="export-google"
                                onClick={syncGoogleCalendar}
                                loading={loading === 2}
                            >
                                <RefreshIcon />
                                Sync Calendar
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {!isAdminManager && (
                <CalendarSelector
                    active={active}
                    onChange={handleActiveChange}
                />
            )}
            <div style={{ position: 'relative', marginTop: '40px' }}>
                <FullCalendar
                    plugins={[listPlugin, dayGridPlugin]}
                    initialView={
                        active === 'Your Calendar'
                            ? 'dayGridMonth'
                            : 'listMonth'
                    }
                    headerToolbar={{
                        left: 'prev,next,today',
                        center: 'title',
                        right:
                            active === 'Your Calendar'
                                ? 'listDay,listWeek,dayGridMonth,listYear'
                                : 'listDay,listWeek,listMonth,listYear',
                    }}
                    customButtons={{
                        listDay: {
                            text: 'Day',
                            click: () => handleChange('listDay'),
                        },
                        listWeek: {
                            text: 'Week',
                            click: () => handleChange('listWeek'),
                        },
                        dayGridMonth: {
                            text: 'Month',
                            click: () => handleChange('dayGridMonth'),
                        },
                        listMonth: {
                            text: 'Month',
                            click: () => handleChange('listMonth'),
                        },
                        listYear: {
                            text: 'Year',
                            click: () => handleChange('listYear'),
                        },
                        today: {
                            text: 'Today',
                            click: () => handleChange('today'),
                        },
                    }}
                    eventClick={handleClick}
                    events={events}
                    allDayText=""
                    eventContent={eventContent}
                    ref={calanderRef}
                />
            </div>
            <Modal
                isOpen={openModal}
                close={() => setOpenModal(false)}
                title="Subscribe to Google Calendar"
                size="small"
            >
                <Typography variant="body">
                    By subscribing, our campaigns will appear directly in your
                    Google Calendar. We’ll add {user?.emailAddress} to the
                    calendar. Please ensure this email is compatible with Google
                    Calendar (e.g., a Gmail or Google Workspace address).
                </Typography>
                <Button
                    color="primary"
                    onClick={handleSubscribeToGoogleCalendar}
                    loading={loading === 1}
                >
                    Confirm
                </Button>
            </Modal>
        </div>
    );
};

export default Calendar;
